import * as React from 'react';
import { FC, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import BalanceFloating from '../atoms/common/BalanceFloating';
import { ReactComponent as PlusCircleIcon } from 'assets/svgs/plus-circle.svg';
import { useNavigate } from 'react-router-dom';
import { CurrencyType, getCurrency } from '../../CurrencyConfig';
import useGetMeOperation from '../../graphql/operations/queries/useGetMeOperation';
import { useLang } from '../../utils/context/LangContext';
import {
  Maybe,
  TransactionType,
  useOnWalletUpdatedSubscription,
  useSaveWalletHistoryIsShownMutation,
  useSearchMyWalletHistoryQuery,
} from '../../graphql/__generated__/graphqlApi';
import formatPond from '../../utils/formatPond';
import useCompleteUserSignInBonusOperation from '../../graphql/operations/mutations/useCompleteUserSignInBonusOperation';
import { WatchQueryFetchPolicy } from '@apollo/client';
import { useDetectUser, UserType } from '../../hooks/useDetectUser';
import { SxProps } from '@mui/material/styles';

const CardContainer = styled(Card)(
  () => `
  box-shadow: 0px 3px 6px rgba(116, 122, 128, 0.2);
  background-color: transparent;
  border-radius: 39px;
  width: max-content;
`,
);
const Content = styled(Stack)(
  ({ theme }) => `
  height: 34px;
  padding: 0 10px 1px 16px;
  border-radius: 39px;
  box-shadow: 0px 3px 6px rgba(116, 122, 128, 0.2);
  background-color: ${theme.colors.white.base};
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`,
);
const Container = styled(Stack)(
  () => `
  position: relative;
  align-items: flex-end;
  padding: 0 24px 0 0;
`,
);
const BonusMessage = styled(Stack)(
  () => `
  background-color: transparent;
  position: absolute;
  z-index: 2;
  flex: 1;
  right: 0;
  bottom: 70px;
`,
);
const BonusMessageTriangle = styled(Stack)(
  ({ theme }) => `
  position: absolute;
  bottom: -12px;
  right: 20px;
  width: 0;
  height: 0;
  border-top: 20px solid ${theme.colors.purple.darkness1};
  border-left: 0 solid transparent;
  border-right: 20px solid transparent;
`,
);

const LinearGradient = styled(Stack)(
  ({ theme }) => `
  width: 284px;
  height: 87px;
  border-radius: 12px;
  background: linear-gradient(${theme.colors.purple.lightness1}, ${theme.colors.purple.darkness1});
`,
);

const PlusCircle = styled(PlusCircleIcon)<{ size: number }>(
  ({ theme, size }) => `
    width: ${size}px;
    height: ${size}px;
    fill: ${theme.colors.purple.base};
`,
);

const BonusMessageOuter = styled(Stack)`
  margin-top: 20px;
`;

const BonusMessageHeading = styled(Typography)(
  ({ theme }) => `
  color: ${theme.colors.white.base};
  text-align: center;
  font-weight: 700;
  font-size: 20px;
  line-height: 20px;
`,
);
const BonusMessageBody = styled(Typography)(
  ({ theme }) => `
  color: ${theme.colors.white.base};
  text-align: center;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  margin-top: 5px;
`,
);
interface BalanceFloatingButtonProps {
  sx?: SxProps;
  isHide?: boolean;
  isTrial?: boolean;
  cashTrial?: number | null;
  fetchPolicy?: WatchQueryFetchPolicy;
}

const BalanceFloatingButton: FC<BalanceFloatingButtonProps> = ({
  sx,
  isHide,
  isTrial,
  cashTrial,
  fetchPolicy,
}) => {
  const navigate = useNavigate();
  const { me, refreshMe, loading } = useGetMeOperation({
    fetchPolicy: fetchPolicy ?? 'no-cache',
  });
  const { wallet } = me || {};
  const { t } = useLang();
  const { userType } = useDetectUser();
  const [bonusAmount, setBonusAmount] = React.useState<
    Maybe<number> | undefined
  >(0);
  const [bonusText, setBonusText] = React.useState<string>('');
  const [showBonusStatus, setShowBonusStatus] = React.useState<boolean>(true);
  const [walletUpdateLoading, setWalletUpdateLoading] =
    React.useState<boolean>(true);
  const [saveWalletHistoryIsShown] = useSaveWalletHistoryIsShownMutation();
  const completeUserSignInBonus = useCompleteUserSignInBonusOperation();

  const { loading: walletUpdatedLoading } = useOnWalletUpdatedSubscription({
    variables: {
      id: me?.id || '',
    },
  });

  const handleTap = useCallback(() => {
    if (getCurrency() === CurrencyType.Energy || isTrial) {
      return;
    }
    navigate('/payment-deposit');
  }, [isTrial, navigate]);

  const { data: myWalletHistory, refetch: myWalletHistoryRefetch } =
    useSearchMyWalletHistoryQuery({
      fetchPolicy: 'network-only',
      variables: {
        params: {
          dateRange: null,
          size: null,
          startKey: null,
          transactionTypes: [TransactionType.Bonus],
        },
      },
      skip: userType !== UserType.USER,
    });

  useEffect(() => {
    const jackpotNotifyArr = myWalletHistory
      ? myWalletHistory.searchMyWalletHistory.history.filter(
          walletHistory => walletHistory && !walletHistory.isShown,
        )
      : [];

    if (jackpotNotifyArr.length > 0) {
      let i = 0;
      (function loop() {
        setTimeout(() => setShowBonusStatus(true), 3000);
        setShowBonusStatus(false);
        setBonusAmount(jackpotNotifyArr[i].amount);
        setBonusText(jackpotNotifyArr[i].text || t('youGotABonus'));
        saveWalletHistoryIsShown({
          variables: {
            input: {
              histories: {
                id: jackpotNotifyArr[i].id,
                isShown: true,
              },
            },
          },
        })
          .then(res => console.log(res))
          .catch(err => console.log(err));
        if (++i < jackpotNotifyArr.length) {
          setTimeout(loop, 4000);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletUpdateLoading]);

  useEffect(() => {
    (async () => {
      if (userType !== UserType.USER) {
        return;
      }
      if (!walletUpdatedLoading) {
        await myWalletHistoryRefetch();
        setWalletUpdateLoading(walletUpdatedLoading);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletUpdatedLoading, myWalletHistory]);

  useEffect(() => {
    (async () => {
      if (!me?.id) return;
      await completeUserSignInBonus();
      await refreshMe();
      await myWalletHistoryRefetch();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.id]);

  const cashFromDeposit = useMemo(() => {
    return cashTrial || wallet?.cashFromDeposit;
  }, [cashTrial, wallet?.cashFromDeposit]);

  if (isHide) return <></>;

  return (
    <Container sx={sx}>
      {!showBonusStatus && (
        <BonusMessage>
          <LinearGradient>
            <BonusMessageOuter>
              <BonusMessageHeading>
                {t('bonusMessage', {
                  bonusCurrency: t('bonus'),
                  bonusAmount: formatPond(bonusAmount ?? 0.0),
                })}
              </BonusMessageHeading>
              <BonusMessageBody>{bonusText}</BonusMessageBody>
            </BonusMessageOuter>
          </LinearGradient>
          <BonusMessageTriangle />
        </BonusMessage>
      )}
      <CardContainer onClick={handleTap}>
        <CardActionArea>
          <Content>
            <Box mr={1}>
              <BalanceFloating
                isLoading={!wallet ? loading : false}
                balance={isTrial ? 0 : wallet?.bonus ?? 0.0}
                colorMode={'light'}
                isBonus
              />
            </Box>
            <BalanceFloating
              isLoading={!wallet ? loading : false}
              balance={cashFromDeposit ?? 0.0}
              colorMode={'light'}
            />
            <PlusCircle size={20} />
          </Content>
        </CardActionArea>
      </CardContainer>
    </Container>
  );
};

export default BalanceFloatingButton;
